import React, { createContext, useCallback, useContext, useState } from 'react';

import { useCookieContext } from '../../context';
import { getGatewayURL } from '../../utils/apiUtils';

export interface Feedback {
  value: 'positive' | 'negative';
  status: 'submitting' | 'success' | 'error';
  timestamp: number;
}

export interface ChatMessage {
  id: number;
  text?: string;
  isUser: boolean;
  agentExecution?: boolean;
  userInput?: string;
  type?: 'text' | 'agentExecution';
  parentMessageId?: number; // New field to store the parent message ID
  feedback?: Feedback; // Feedback data for agent responses
  metrics?: {
    responseTime?: number;
    tokenCount?: number;
  };
}

interface MessagesContextValue {
  messages: ChatMessage[];
  addMessage: (message: ChatMessage) => void;
  clearMessages: () => void; // Method to clear all messages
  setAgentExecuting: (isExecuting: boolean) => void;
  agentExecuting: boolean;
  submitFeedback: (messageId: number, value: 'positive' | 'negative') => Promise<void>;
  addResponseToAgentMessage: (response: string, messageId: number) => void;
  sessionId: string;
}

const MessagesContext = createContext<MessagesContextValue | undefined>(undefined);

const GATEWAY_URL = getGatewayURL();

const submitFeedbackToServer = async (
  feedbackData: {
    response: string;
    rating: number;
    prompt: string;
    id: number;
  },
  bearer: string
) => {
  try {
    const response = await fetch(`${GATEWAY_URL}/support-agent/api/v1/messageFeedback`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: bearer,
      },
      body: JSON.stringify(feedbackData),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error submitting feedback:', error);
    throw error;
  }
};

export const MessagesProvider = ({ children }: { children: React.ReactNode }) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [agentExecuting, setAgentExecuting] = useState<boolean>(false);
  const [sessionId] = useState<string>(crypto.randomUUID());
  const { cookie } = useCookieContext();

  const addMessage = useCallback(
    (message: ChatMessage) => {
      setMessages((prevMessages) => {
        // If this is an agent message, find the last user message as parent
        if (!message.isUser && prevMessages.length > 0) {
          const parentMessage = [...prevMessages].reverse().find((msg) => msg.isUser);

          if (parentMessage) {
            message.parentMessageId = parentMessage.id;
          }
        }

        return [
          ...prevMessages,
          {
            ...message,
            sessionId,
          },
        ];
      });
    },
    [sessionId]
  );

  const addResponseToAgentMessage = (response: string, messageId: number) => {
    setMessages((prevMessages) =>
      prevMessages.map((msg) =>
        msg.id === messageId
          ? {
              ...msg,
              text: response,
            }
          : msg
      )
    );
  };

  const clearMessages = () => {
    setMessages([]);
  };

  const submitFeedback = async (messageId: number, value: 'positive' | 'negative') => {
    try {
      const bearer = cookie || '';
      // Find the message and its parent
      const message = messages.find((msg) => msg.id === messageId);
      if (!message) {
        // eslint-disable-next-line no-console
        console.error('Message not found');
        return;
      }

      setMessages((prevMessages) =>
        prevMessages.map((msg) =>
          msg.id === messageId
            ? {
                ...msg,
                feedback: {
                  value,
                  status: 'submitting',
                  timestamp: Date.now(),
                },
              }
            : msg
        )
      );

      const feedbackData = {
        id: messageId,
        rating: value === 'positive' ? 1 : 0,
        prompt: message.userInput || '',
        response: message.text || '',
      };

      // Submit to API
      await submitFeedbackToServer(feedbackData, bearer);

      setMessages((prevMessages) =>
        prevMessages.map((msg) =>
          msg.id === messageId
            ? {
                ...msg,
                feedback: {
                  value,
                  status: 'success',
                  timestamp: Date.now(),
                },
              }
            : msg
        )
      );
    } catch (error) {
      setMessages((prevMessages) =>
        prevMessages.map((msg) =>
          msg.id === messageId
            ? {
                ...msg,
                feedback: {
                  value,
                  status: 'error',
                  timestamp: Date.now(),
                },
              }
            : msg
        )
      );
    }
  };

  return (
    <MessagesContext.Provider
      value={{
        messages,
        addMessage,
        clearMessages,
        setAgentExecuting,
        agentExecuting,
        submitFeedback,
        sessionId,
        addResponseToAgentMessage,
      }}
    >
      {children}
    </MessagesContext.Provider>
  );
};

export const useMessagesContext = () => {
  const context = useContext(MessagesContext);
  if (!context) {
    throw new Error('useMessagesContext must be used within a MessagesProvider');
  }
  return context;
};
